import { useEffect, useId, useRef } from "react";
import { useLocale } from "../i18n/useLocale";
import { getFocusableElements } from "./focusTrap";
import { IconButton } from "./IconButton";

export function Modal({
  title,
  subtitle,
  titleId: titleIdProp,
  onClose,
  children,
  header,
  className = "modal",
  backdropClassName = "modal-backdrop",
}) {
  const { display } = useLocale();
  const generatedId = useId();
  const titleId = titleIdProp || `${generatedId}-title`;
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const focusables = getFocusableElements(dialog);
    (focusables[0] || dialog).focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const elements = getFocusableElements(dialog);
      if (!elements.length) return;

      const first = elements[0];
      const last = elements[elements.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className={backdropClassName}
      role="presentation"
      onMouseDown={onClose}
    >
      <div
        ref={dialogRef}
        className={className}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {header || (
          <div className="modal-title">
            <div>
              <h2 id={titleId}>{title}</h2>
              {subtitle && <p>{subtitle}</p>}
            </div>
            <IconButton label={display.t("web.shell.closeNotice")} onClick={onClose}>×</IconButton>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
